import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, tasksApi } from '../../lib/api.ts'
import { PRIORITY_LABEL, type Priority, type Task } from '../../lib/types.ts'
import {
  useArchiveTask,
  useColumns,
  useUpdateTask,
  useUsers,
  taskKeys,
} from '../../hooks/useTasks.ts'
import { ConfirmDialog } from '../../ui/ConfirmDialog.tsx'
import { renderMarkdown } from '../../lib/markdown.ts'
import { MentionField } from '../../ui/MentionField.tsx'
import { ErrorState, LabelChip, Skeleton, StatusPill } from '../../ui/primitives.tsx'
import { CommentComposer, CommentThread } from '../comments/CommentThread.tsx'
import { TaskNotes } from './TaskNotes.tsx'
import styles from './drawer.module.css'

/**
 * ONE component, shared by the board and (from stage 3) the table. Do not grow a "table variant" —
 * that is exactly the anti-pattern RULES §5 names.
 */
export function TaskDrawer({
  taskId,
  onClose,
}: {
  taskId: number | null
  onClose: () => void
}): React.JSX.Element | null {
  const columns = useColumns()
  const users = useUsers()
  const updateTask = useUpdateTask()
  const archiveTask = useArchiveTask()
  const [confirmArchive, setConfirmArchive] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const task = useQuery({
    queryKey: taskId === null ? ['task', 'none'] : taskKeys.one(taskId),
    // `enabled` below already guarantees this, but the type system cannot see the link between
    // the two options — an explicit guard says so without throwing the check away with `!`.
    queryFn: async () => {
      if (taskId === null) throw new Error('Task drawer queried with no task id.')
      return (await tasksApi.get(taskId)).task
    },
    enabled: taskId !== null,
  })

  /**
   * The `e` shortcut lives here, not in useHotkeys, because the confirmation it opens lives here.
   * A shortcut that archives on one keypress is the same trap the "⋯" button was, only faster.
   */
  useEffect(() => {
    if (taskId === null) return

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'e' || event.metaKey || event.ctrlKey || event.altKey) return

      const target = event.target
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      if (typing) return

      event.preventDefault()
      setConfirmArchive(true)
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [taskId])

  /**
   * Focus management for a modal (ui-spec §11), which is three things, not one:
   *
   *  1. move focus in, so Tab order follows what is on screen;
   *  2. keep it in — without the trap below, Tab walks straight out of the drawer and onto the
   *     top bar *underneath the scrim*, where the caret is invisible and the clicks do nothing;
   *  3. give it back on close, otherwise focus resets to <body> and the next Tab restarts from
   *     the top of the document instead of the card the drawer was opened from.
   */
  useEffect(() => {
    if (taskId === null) return

    const returnTo = document.activeElement as HTMLElement | null
    panelRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return

      // Queried on each Tab, not cached: the drawer's contents change as the task loads.
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      returnTo?.focus()
    }
  }, [taskId])

  if (taskId === null) return null

  const data = task.data
  const conflict = updateTask.error instanceof ApiError && updateTask.error.status === 409

  return (
    <>
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label={data ? data.title : 'Szczegóły zadania'}
        tabIndex={-1}
      >
        <header className={styles.head}>
          <span className={styles.ref}>{data?.ref ?? `T-${taskId}`}</span>
          {data &&
            (() => {
              const column = columns.data?.find((c) => c.id === data.columnId)
              return column ? <StatusPill column={column} /> : null
            })()}
          <span className={styles.headSpacer} />
          {/* Nazwany przycisk, nie ikona. „⋯" znaczy wszędzie indziej „opcje", więc ludzie klikali
              go, żeby zobaczyć, co tam jest — i tracili zadanie, bo archiwizacja działa się od razu.
              Przywracanie nie pyta o nic: ono niczego nie zabiera. */}
          {data?.archivedAt ? (
            <button
              type="button"
              className={styles.restoreButton}
              onClick={() => {
                archiveTask.mutate({ id: taskId, archived: false })
              }}
            >
              Przywróć
            </button>
          ) : (
            <button
              type="button"
              className={styles.archiveButton}
              title="Archiwizuj (e)"
              onClick={() => setConfirmArchive(true)}
            >
              Archiwizuj
            </button>
          )}
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Zamknij szczegóły zadania"
            title="Zamknij (Esc)"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className={styles.body}>
          {task.isError ? (
            // Previously any failure fell through to the "Wczytywanie…" branch below and stayed
            // there forever, because `data` is undefined whether the request is in flight or dead.
            <ErrorState
              text="Nie udało się wczytać tego zadania."
              onRetry={() => void task.refetch()}
            />
          ) : !data ? (
            // Skeletons in the shape of the content, not a word (ui-spec §7).
            <>
              <Skeleton height={28} delay={0} />
              <Skeleton delay={200} />
              <Skeleton delay={400} />
            </>
          ) : (
            <DrawerBody
              // Switching tasks remounts the body, which is what resets the title and description
              // drafts. Doing it in an effect instead would carry a half-typed title from one task
              // into the next, because the drafts below deliberately survive incoming changes.
              key={data.id}
              task={data}
              columns={columns.data ?? []}
              users={users.data ?? []}
              conflict={conflict}
              onPatch={(patch) => updateTask.mutate({ id: taskId, patch })}
              onRefresh={() => void task.refetch()}
            />
          )}
        </div>

        {data && <CommentComposer taskId={data.id} />}
      </div>

      <ConfirmDialog
        open={confirmArchive}
        title={`Przenieść ${data?.ref ?? `T-${taskId}`} do archiwum?`}
        body="Zadanie zniknie z boardu. Znajdziesz je w tabeli po włączeniu filtra Archiwum i stamtąd możesz je przywrócić."
        confirmLabel="Archiwizuj"
        onCancel={() => setConfirmArchive(false)}
        onConfirm={() => {
          setConfirmArchive(false)
          archiveTask.mutate({ id: taskId, archived: true })
          onClose()
        }}
      />
    </>
  )
}

function DrawerBody({
  task,
  columns,
  users,
  conflict,
  onPatch,
  onRefresh,
}: {
  task: Task
  columns: {
    id: number
    name: string
    color: 'gray' | 'blue' | 'steel' | 'green'
    position: number
  }[]
  users: { id: number; displayName: string }[]
  conflict: boolean
  onPatch: (patch: Parameters<typeof tasksApi.patch>[1]) => void
  onRefresh: () => void
}): React.JSX.Element {
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.descriptionMd)
  const [editingDescription, setEditingDescription] = useState(false)
  const lastServerTitle = useRef(task.title)
  const lastServerDescription = useRef(task.descriptionMd)

  /**
   * Someone else (or an agent) may change the task while the drawer is open, and we want to see
   * that — but not at the cost of what is being typed right now.
   *
   * Two separate effects, because one effect over both fields meant a remote change to the
   * *description* re-ran the reset and wiped a half-typed *title*. And each adopts the incoming
   * value only when the local draft still matches what the server last sent: if the draft has
   * diverged, the person is mid-edit and their text wins until they commit or blur.
   */
  useEffect(() => {
    setTitle((draft) => (draft === lastServerTitle.current ? task.title : draft))
    lastServerTitle.current = task.title
  }, [task.title])

  useEffect(() => {
    setDescription((draft) =>
      draft === lastServerDescription.current ? task.descriptionMd : draft,
    )
    lastServerDescription.current = task.descriptionMd
  }, [task.descriptionMd])

  const commitTitle = (): void => {
    const next = title.trim()
    if (!next || next === task.title) return
    onPatch({ title: next, updatedAt: task.updatedAt })
  }

  return (
    <>
      <input
        className={styles.title}
        // Wspólny limit REST i MCP spadł do 300 znaków (apps/server/src/validation.ts). Bez
        // maxLength człowiek dowiadywałby się o nim dopiero z odrzuconego zapisu, po napisaniu.
        maxLength={300}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commitTitle}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        aria-label="Tytuł zadania"
      />

      <div className={styles.metaGrid}>
        <span className={styles.metaLabel}>Status</span>
        <div className={styles.metaValue}>
          <select
            className={styles.select}
            value={task.columnId}
            onChange={(e) => onPatch({ columnId: Number(e.target.value) })}
            aria-label="Status"
          >
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <span className={styles.metaLabel}>Osoba</span>
        <div className={styles.metaValue}>
          <select
            className={styles.select}
            value={task.assigneeId ?? ''}
            onChange={(e) =>
              onPatch({ assigneeId: e.target.value === '' ? null : Number(e.target.value) })
            }
            aria-label="Przypisana osoba"
          >
            <option value="">nikt</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </div>

        <span className={styles.metaLabel}>Priorytet</span>
        <div className={styles.metaValue}>
          <select
            className={styles.select}
            value={task.priority}
            onChange={(e) => onPatch({ priority: Number(e.target.value) as Priority })}
            aria-label="Priorytet"
          >
            {([1, 2, 3] as Priority[]).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </select>
        </div>

        <span className={styles.metaLabel}>Termin</span>
        <div className={styles.metaValue}>
          <input
            className={styles.dateInput}
            type="date"
            value={task.dueDate ?? ''}
            onChange={(e) => onPatch({ dueDate: e.target.value === '' ? null : e.target.value })}
            aria-label="Termin"
          />
        </div>

        <span className={styles.metaLabel}>Labelki</span>
        <div className={styles.metaValue}>
          {/* Do tego etapu labelek nie dało się dodać ani usunąć z UI: `TaskPatch.labels` istniał
              w api.ts, ale żaden komponent go nie wysyłał, więc filtr „Labelka" na świeżej
              instalacji zawsze pokazywał „wszystkie". */}
          {task.labels.map((l) => (
            <LabelChip
              key={l}
              onRemove={() =>
                onPatch({ labels: task.labels.filter((x) => x !== l), updatedAt: task.updatedAt })
              }
            >
              {l}
            </LabelChip>
          ))}
          <LabelAdd
            existing={task.labels}
            onAdd={(label) =>
              onPatch({ labels: [...task.labels, label], updatedAt: task.updatedAt })
            }
          />
        </div>

        {/* Ostatni wiersz, bo to jedyna metadana, której nie da się tu zmienić — czyta się,
            nie ustawia. */}
        <span className={styles.metaLabel}>Utworzono</span>
        <div className={styles.metaValue}>
          <span className={styles.metaReadonly}>{formatCreatedAt(task.createdAt)}</span>
        </div>
      </div>

      {/* Opis jest markdownem — i tak też ma wyglądać. Wcześniej stało tu zawsze pole edycji,
          więc `**Stan**` i backticki wyświetlały się dosłownie, a opis pisany przez agenta MCP
          czytało się jak plik źródłowy. Edycja po kliknięciu „edytuj", tak jak w komentarzach. */}
      <div className={styles.sectionRow}>
        <p className={styles.sectionLabel}>Opis</p>
        {!editingDescription && (
          <button
            type="button"
            className={styles.sectionAction}
            onClick={() => setEditingDescription(true)}
          >
            edytuj
          </button>
        )}
      </div>

      {editingDescription ? (
        <MentionField
          className={styles.description}
          value={description}
          placeholder="Opis w markdownie…"
          onChange={setDescription}
          onBlur={() => {
            setEditingDescription(false)
            if (description !== task.descriptionMd) onPatch({ descriptionMd: description })
          }}
          aria-label="Opis"
        />
      ) : description.trim() === '' ? (
        <button
          type="button"
          className={styles.descriptionEmpty}
          onClick={() => setEditingDescription(true)}
        >
          Dodaj opis…
        </button>
      ) : (
        <DescriptionBody markdown={description} />
      )}

      {conflict && (
        <p className={styles.conflict}>
          Ktoś zmienił to zadanie przed chwilą. Odśwież, żeby zobaczyć aktualną wersję.
          <button type="button" className={styles.conflictAction} onClick={onRefresh}>
            Odśwież
          </button>
        </p>
      )}

      {/* Nad komentarzami, bo notatka to materiał do pracy, a komentarze to rozmowa o niej —
          i bo dyskusja rośnie w nieskończoność, więc cokolwiek pod nią, znika. */}
      <TaskNotes taskId={task.id} />

      <div className={styles.commentsPlaceholder}>
        <p className={styles.sectionLabel}>Komentarze</p>
        <CommentThread taskId={task.id} />
      </div>
    </>
  )
}

/** Pełna data z godziną — w drawerze jest miejsce, a „29.07" nie odpowiada na „o której". */
function formatCreatedAt(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * The `+` chip from ui-components.md. Deliberately not a free-text field that is always visible:
 * labels are a small closed set in practice, and an always-open input invites typos that then look
 * like new labels in the filter.
 */
function LabelAdd({
  existing,
  onAdd,
}: {
  existing: string[]
  onAdd: (label: string) => void
}): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')

  const commit = (): void => {
    const value = draft.trim()
    // Duplicates are dropped here rather than server-side: the array column would happily hold two
    // identical entries and the filter would then show the same label twice.
    if (value && !existing.includes(value)) onAdd(value)
    setDraft('')
    setOpen(false)
  }

  if (!open) {
    return (
      <button
        type="button"
        className={styles.labelAdd}
        aria-label="Dodaj labelkę"
        onClick={() => setOpen(true)}
      >
        +
      </button>
    )
  }

  return (
    <input
      className={styles.labelInput}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- pole pojawia się po kliknięciu „+"
      autoFocus
      value={draft}
      maxLength={60}
      aria-label="Nazwa labelki"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit()
        if (e.key === 'Escape') {
          setDraft('')
          setOpen(false)
        }
      }}
    />
  )
}

/**
 * The description, rendered.
 *
 * Collapsed past a certain height on purpose: a long description — and the ones an agent writes are
 * long — would otherwise push the comments so far down that nobody would know a discussion exists.
 * The clamp is only applied when the content actually overflows, so short descriptions get no
 * chrome they do not need.
 */
function DescriptionBody({ markdown }: { markdown: string }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const body = useRef<HTMLDivElement>(null)

  const html = useMemo(() => renderMarkdown(markdown), [markdown])

  useEffect(() => {
    const element = body.current
    if (!element) return
    setOverflows(element.scrollHeight > element.clientHeight + 4)
  }, [html, expanded])

  return (
    <div className={styles.descriptionWrap}>
      <div
        ref={body}
        className={`markdown ${expanded ? styles.descriptionRead : styles.descriptionReadClamped}`}
        // Safe by construction: markdown-it runs with html:false and an allow-list of link
        // schemes, so nothing here can carry markup of its own (lib/markdown.ts).
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {(overflows || expanded) && (
        <button
          type="button"
          className={styles.descriptionToggle}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Zwiń opis' : 'Pokaż cały opis'}
        </button>
      )}
    </div>
  )
}
